package com.example.histoire.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import io.ktor.client.HttpClient
import kotlinx.browser.localStorage
import kotlinx.browser.sessionStorage
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.await
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.StyleScope
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.percent
import kotlin.js.Promise

/** Après envoi réussi : rechargement puis affichage du message de confirmation (une fois). */
private const val MAGIC_LINK_SENT_CONFIRM_KEY = "histoire_magic_link_sent_ok_v1"
private const val SIGNUP_LINK_PENDING_EMAIL_KEY = "histoire_signup_link_pending_email_v1"
private const val SIGNUP_LINK_RESEND_AT_MS_KEY = "histoire_signup_link_resend_at_ms_v1"
private const val SIGNUP_RESEND_COOLDOWN_MS = 45_000L
private const val PASSKEY_ENABLED_HINT_KEY = "histoire_passkey_enabled_hint_v1"
private const val LAST_LOGIN_EMAIL_KEY = "histoire_last_login_email_v1"
private const val PASSKEY_LAST_CHALLENGE_ID_KEY = "histoire_passkey_last_challenge_id_v1"
private const val PASSKEY_AUTO_RETRY_DELAY_MS = 12_000L
private const val FORM_FOCUS_SCROLL_JS =
    "this.scrollIntoView({behavior:'smooth', block:'center'});"
private const val FORM_ENTER_NEXT_JS =
    "if(event.key==='Enter'){event.preventDefault();const scope=this.closest('[data-enter-scope]')||document;" +
        "const els=[...scope.querySelectorAll('input,button,textarea,select')]" +
        ".filter(e=>!e.disabled&&e.offsetParent!==null);" +
        "const i=els.indexOf(this);if(i>=0&&i<els.length-1){els[i+1].focus({preventScroll:true});" +
        "els[i+1].scrollIntoView({behavior:'smooth',block:'center'});}}"

private data class PasskeyAuthResult(
    val session: PasskeySessionPayload? = null,
    val errorMessage: String? = null,
)

private data class PasskeyFunctionCallResult(
    val ok: Boolean,
    val parsed: dynamic = null,
    val error: String? = null,
)

/** Saisie OTP ou collage du lien Supabase (token / token_hash dans l’URL). */
private fun recoveryOtpFieldReadyForSubmit(raw: String): Boolean {
    val t = raw.trim()
    if (t.isEmpty()) return false
    val lower = t.lowercase()
    val linkish =
        "://" in t ||
            "token_hash=" in lower ||
            "type=recovery" in lower ||
            t.length > 48
    if (linkish) return t.length >= 12
    return t.filter { ch -> ch.isDigit() }.length == SUPABASE_WEB_EMAIL_OTP_DIGITS
}

private fun urlHasResetFlowHint(): Boolean {
    val hash = window.location.hash.removePrefix("#").lowercase()
    val search = window.location.search.removePrefix("?").lowercase()
    val all = "$hash&$search"
    return "type=recovery" in all
}

private fun signupFailureMessageForDisplay(raw: String): String {
    val lower = raw.lowercase()
    val duplicate =
        "already been registered" in lower ||
            "already registered" in lower ||
            "user already registered" in lower ||
            "email address is already registered" in lower ||
            "duplicate" in lower
    return if (duplicate) {
        "Un compte existe déjà pour cet e-mail. Choisis « En fait, je possède déjà un compte » et connecte-toi " +
            "avec ton mot de passe (aucun e-mail envoyé depuis cette action)."
    } else {
        raw
    }
}

private enum class EmailAuthStep {
    /** Deux boutons : création vs connexion */
    Choose,

    /** Formulaire e-mail — inscription */
    CreateAccount,

    /** Formulaire e-mail — compte existant */
    ExistingAccount,

    /** Réinitialisation du mot de passe après clic sur le lien e-mail. */
    ResetPassword,

    /** Écran intermédiaire: l'utilisateur confirme avant consommation du lien recovery. */
    RecoveryLinkGate,

    /** Écran de rattrapage si le popup a été fermé / page quittée. */
    ConfirmationLinkHelp,
}

private data class LoginEntryState(
    val showSentBanner: Boolean,
    val initialStep: EmailAuthStep,
    val recoveryPending: Boolean,
    val recoveryNeedsUserConfirm: Boolean,
)

@Composable
fun SupabaseEmailLoginCard(
    client: HttpClient,
    onLoggedIn: () -> Unit,
) {
    val entry = remember {
        val pendingFlag = consumeRecoveryPendingFlag()
        val uiLock = hasRecoveryUiLock()
        val recoveryFromUrl = urlHasResetFlowHint()
        val recoveryPending = pendingFlag || uiLock
        // Parcours OTP: si un lien recovery est ouvert, aller directement vers l'écran code
        // sans afficher l'étape "lien déjà utilisé / expiré".
        val recoveryNeedsUserConfirm = false
        val raw = localStorage.getItem(MAGIC_LINK_SENT_CONFIRM_KEY)
        val banner = !raw.isNullOrBlank()
        if (banner) {
            localStorage.removeItem(MAGIC_LINK_SENT_CONFIRM_KEY)
        }
        LoginEntryState(
            showSentBanner = banner,
            initialStep =
                when {
                    recoveryFromUrl || recoveryPending -> EmailAuthStep.ResetPassword
                    banner -> EmailAuthStep.ExistingAccount
                    else -> EmailAuthStep.Choose
                },
            recoveryPending = recoveryPending,
            recoveryNeedsUserConfirm = recoveryNeedsUserConfirm,
        )
    }
    var step by remember { mutableStateOf(entry.initialStep) }
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var passwordConfirm by remember { mutableStateOf("") }
    var recoveryOtpCode by remember { mutableStateOf("") }
    var signupOtpCode by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }
    var successMessage by remember { mutableStateOf<String?>(null) }
    var busy by remember { mutableStateOf(false) }
    var recoveryFlowActive by remember { mutableStateOf(entry.recoveryPending) }
    var recoveryNeedsUserConfirm by remember { mutableStateOf(entry.recoveryNeedsUserConfirm) }
    var confirmationEmail by remember { mutableStateOf(localStorage.getItem(SIGNUP_LINK_PENDING_EMAIL_KEY) ?: "") }
    var confirmationHelpBackStep by remember { mutableStateOf(EmailAuthStep.ExistingAccount) }
    var confirmationDialogOpen by remember { mutableStateOf(false) }
    var resendAvailableAtMs by remember {
        mutableStateOf(localStorage.getItem(SIGNUP_LINK_RESEND_AT_MS_KEY)?.toLongOrNull() ?: 0L)
    }
    var nowMs by remember { mutableStateOf(js("Date.now()").unsafeCast<Double>().toLong()) }
    val passkeySupported = remember { isWebAuthnSupportedInBrowser() }
    var passkeyHintEnabled by remember { mutableStateOf(localStorage.getItem(PASSKEY_ENABLED_HINT_KEY) == "1") }
    val passkeyBlockedByPolicy = isPasskeyBlockedUntilPasswordLogin()
    var passkeyAutoAttempted by remember { mutableStateOf(false) }
    var passkeyAutoFailedAtMs by remember { mutableStateOf<Long?>(null) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(step) {
        password = ""
        passwordConfirm = ""
        recoveryOtpCode = ""
        signupOtpCode = ""
        successMessage = null
    }
    LaunchedEffect(recoveryFlowActive) {
        if (recoveryFlowActive && step != EmailAuthStep.ResetPassword) {
            step = EmailAuthStep.ResetPassword
        }
    }
    LaunchedEffect(recoveryNeedsUserConfirm, recoveryFlowActive) {
        if (recoveryNeedsUserConfirm && !recoveryFlowActive && step != EmailAuthStep.RecoveryLinkGate) {
            step = EmailAuthStep.RecoveryLinkGate
        }
    }
    LaunchedEffect(recoveryFlowActive) {
        if (!recoveryFlowActive) return@LaunchedEffect
        val recoveryEmail = currentSupabaseAuthEmail(client)
        if (!recoveryEmail.isNullOrBlank()) {
            email = recoveryEmail
        }
    }
    LaunchedEffect(resendAvailableAtMs) {
        while (resendAvailableAtMs > nowMs) {
            delay(1000)
            nowMs = js("Date.now()").unsafeCast<Double>().toLong()
        }
    }
    LaunchedEffect(step) {
        if (step == EmailAuthStep.ExistingAccount) {
            passkeyAutoAttempted = false
        }
    }
    LaunchedEffect(passkeyHintEnabled, passkeySupported, passkeyBlockedByPolicy, recoveryFlowActive) {
        if (recoveryFlowActive) return@LaunchedEffect
        if (!passkeyHintEnabled || !passkeySupported || passkeyBlockedByPolicy) return@LaunchedEffect
        val remembered = localStorage.getItem(LAST_LOGIN_EMAIL_KEY)?.trim().orEmpty()
        if (!remembered.contains('@')) return@LaunchedEffect
        if (email.trim().isBlank()) {
            email = remembered
        }
        if (step == EmailAuthStep.Choose) {
            error = null
            successMessage = null
            step = EmailAuthStep.ExistingAccount
        }
    }
    LaunchedEffect(step, passkeyHintEnabled, passkeySupported, passkeyBlockedByPolicy, recoveryFlowActive) {
        if (step != EmailAuthStep.ExistingAccount) return@LaunchedEffect
        if (recoveryFlowActive) return@LaunchedEffect
        if (busy || passkeyAutoAttempted) return@LaunchedEffect
        if (!passkeyHintEnabled || !passkeySupported || passkeyBlockedByPolicy) return@LaunchedEffect
        val now = js("Date.now()").unsafeCast<Double>().toLong()
        val failedAt = passkeyAutoFailedAtMs
        if (failedAt != null) {
            val elapsed = now - failedAt
            if (elapsed in 0 until PASSKEY_AUTO_RETRY_DELAY_MS) return@LaunchedEffect
            passkeyAutoFailedAtMs = null
        }
        val remembered = localStorage.getItem(LAST_LOGIN_EMAIL_KEY)?.trim().orEmpty()
        val targetEmail = email.trim().ifBlank { remembered }
        if (!targetEmail.contains('@')) return@LaunchedEffect
        email = targetEmail
        passkeyAutoAttempted = true
        error = null
        successMessage = null
        val passkey = try {
            authenticateWithPasskey(targetEmail)
        } catch (_: CancellationException) {
            return@LaunchedEffect
        } catch (t: Throwable) {
            val details = t.message?.trim().orEmpty().take(180)
            PasskeyAuthResult(
                errorMessage = if (details.isBlank()) {
                    "Connexion biométrique temporairement indisponible."
                } else {
                    "Connexion biométrique temporairement indisponible: $details"
                },
            )
        }
        if (passkey.session == null) {
            // Ne jamais bloquer le formulaire mot de passe après une tentative auto.
            val msg = passkey.errorMessage ?: "Connexion biométrique non disponible. Utilise ton mot de passe."
            passkeyAutoFailedAtMs = now
            error = msg
            return@LaunchedEffect
        }
        saveSupabaseSessionFromPasskey(passkey.session)
        localStorage.setItem(PASSKEY_ENABLED_HINT_KEY, "1")
        localStorage.setItem(LAST_LOGIN_EMAIL_KEY, targetEmail)
        passkeyAutoFailedAtMs = null
        passkeyHintEnabled = true
        markInTabAppUnlockDone()
        onLoggedIn()
    }

    val resendRemainingSeconds = ((resendAvailableAtMs - nowMs).coerceAtLeast(0L) + 999L) / 1000L
    val resendAllowed = resendRemainingSeconds <= 0L

    Div({
        attr("data-enter-scope", "auth-form")
        style { emailLoginCard() }
    }) {
        Div({ style { emailLoginHeroWrap() } }) {
            Img(src = "/histoire-auth-hero.png", attrs = {
                attr("alt", "Livre magique et lune — Histoire")
                style { emailLoginHeroImg() }
            })
        }
        if (entry.showSentBanner) {
            Div({ style { emailSentBanner() } }) {
                P({ style { emailSentBannerTitle() } }) {
                    Text("E-mail envoyé")
                }
                P({ style { emailSentBannerBody() } }) {
                    Text(
                        "Si tu viens de recevoir un message de notre part, suis les instructions dans le mail. " +
                            "Ensuite connecte-toi ci-dessous avec ton e-mail et ton mot de passe.",
                    )
                }
            }
        }

        when (step) {
            EmailAuthStep.Choose -> {
                H1({ style { emailLoginTitle() } }) {
                    Text("Bienvenue")
                }
                P({ style { emailLoginBody() } }) {
                    Text("Choisis la suite : créer un compte ou te connecter avec ton e-mail et ton mot de passe.")
                }
                Button(attrs = {
                    attr("type", "button")
                    onClick {
                        error = null
                        step = EmailAuthStep.CreateAccount
                    }
                    style { emailLoginLandingPrimary() }
                }) { Text("Je crée un compte") }
                Button(attrs = {
                    attr("type", "button")
                    onClick {
                        error = null
                        step = EmailAuthStep.ExistingAccount
                    }
                    style { emailLoginLandingSecondary() }
                }) { Text("Je me connecte") }
                Button(attrs = {
                    attr("type", "button")
                    onClick {
                        error = null
                        successMessage = null
                        recoveryNeedsUserConfirm = false
                        recoveryFlowActive = false
                        confirmationHelpBackStep = EmailAuthStep.Choose
                        step = EmailAuthStep.ConfirmationLinkHelp
                    }
                    style { emailLoginLandingSecondary() }
                }) { Text("Code de confirmation") }
            }
            EmailAuthStep.CreateAccount -> {
                Button(attrs = {
                    attr("type", "button")
                    onClick {
                        error = null
                        step = EmailAuthStep.Choose
                    }
                    style { emailLoginBack() }
                }) { Text("← Retour") }
                H1({ style { emailLoginTitle() } }) {
                    Text("Créer un compte")
                }
                P({ style { emailLoginBody() } }) {
                    Text(
                        "Indique ton e-mail et choisis un mot de passe (au moins 6 caractères). " +
                            "Tu recevras un lien de confirmation cliquable par e-mail (pense à vérifier les spams).",
                    )
                }
                emailPasswordSignUpForm(
                    email = email,
                    onEmail = { email = it },
                    password = password,
                    onPassword = { password = it },
                    passwordConfirm = passwordConfirm,
                    onPasswordConfirm = { passwordConfirm = it },
                    busy = busy,
                    error = error,
                    successMessage = successMessage,
                    onSubmit = {
                        if (busy) return@emailPasswordSignUpForm
                        scope.launch {
                            busy = true
                            error = null
                            successMessage = null
                            try {
                                val em = email.trim()
                                if (em.isEmpty() || !em.contains('@')) {
                                    error = "Merci d’indiquer une adresse e-mail valide."
                                    return@launch
                                }
                                if (password != passwordConfirm) {
                                    error = "Les deux mots de passe ne sont pas identiques."
                                    return@launch
                                }
                                when (checkSupabaseAccountEmailRegistered(client, em)) {
                                    true -> {
                                        error =
                                            "Un compte existe déjà pour cet e-mail. Aucun message n’est envoyé : " +
                                                "choisis « En fait, je possède déjà un compte » et connecte-toi avec ton mot de passe."
                                        return@launch
                                    }
                                    null -> { /* vérif indisponible : on tente quand même l’inscription */ }
                                    false -> { /* nouveau compte : suite */ }
                                }
                                when (val r = signUpSupabaseWithPassword(client, em, password)) {
                                    SupabasePasswordSignUpResult.LoggedIn -> {
                                        markInTabAppUnlockDone()
                                        onLoggedIn()
                                    }
                                    SupabasePasswordSignUpResult.AwaitingEmailConfirmation -> {
                                        confirmationEmail = em
                                        localStorage.setItem(SIGNUP_LINK_PENDING_EMAIL_KEY, em)
                                        email = em
                                        step = EmailAuthStep.ExistingAccount
                                        confirmationDialogOpen = true
                                    }
                                    is SupabasePasswordSignUpResult.Failure ->
                                        error = signupFailureMessageForDisplay(r.message)
                                }
                            } finally {
                                busy = false
                            }
                        }
                    },
                    submitLabel = if (busy) "Création…" else "Créer mon compte",
                )
                Button(attrs = {
                    attr("type", "button")
                    onClick {
                        error = null
                        step = EmailAuthStep.ExistingAccount
                    }
                    style { emailLoginSwitchChoice() }
                }) { Text("En fait, je possède déjà un compte") }
            }
            EmailAuthStep.ExistingAccount -> {
                if (!entry.showSentBanner) {
                    Button(attrs = {
                        attr("type", "button")
                        onClick {
                            error = null
                            successMessage = null
                            step = EmailAuthStep.Choose
                        }
                        style { emailLoginBack() }
                    }) { Text("← Retour") }
                }
                H1({ style { emailLoginTitle() } }) {
                    Text("Connexion")
                }
                P({ style { emailLoginBody() } }) {
                    Text(
                        "Connecte-toi avec ton e-mail et ton mot de passe. " +
                            "Si tu as oublié ton mot de passe, utilise le lien de réinitialisation juste en dessous.",
                    )
                }
                emailPasswordSignInForm(
                    email = email,
                    onEmail = { email = it },
                    password = password,
                    onPassword = { password = it },
                    busy = busy,
                    error = error,
                    successMessage = successMessage,
                    onSubmit = {
                        if (busy) return@emailPasswordSignInForm
                        scope.launch {
                            busy = true
                            error = null
                            successMessage = null
                            try {
                                val passkeyWasBlockedBeforeLogin = isPasskeyBlockedUntilPasswordLogin()
                                val err = signInSupabaseWithPassword(client, email.trim(), password)
                                if (err == null) {
                                    if (passkeyWasBlockedBeforeLogin) {
                                        markBiometricReenabledNoticePending()
                                    }
                                    localStorage.setItem(LAST_LOGIN_EMAIL_KEY, email.trim())
                                    markInTabAppUnlockDone()
                                    onLoggedIn()
                                } else {
                                    error = err
                                }
                            } finally {
                                busy = false
                            }
                        }
                    },
                    onForgotPassword = {
                        if (busy) return@emailPasswordSignInForm
                        scope.launch {
                            busy = true
                            error = null
                            successMessage = null
                            try {
                                val em = email.trim()
                                if (em.isEmpty() || !em.contains('@')) {
                                    error = "Indique d’abord ton e-mail ci-dessus."
                                    return@launch
                                }
                                val err = sendSupabasePasswordResetEmail(client, em)
                                if (err != null) {
                                    error = err
                                } else {
                                    recoveryFlowActive = false
                                    step = EmailAuthStep.ResetPassword
                                    successMessage =
                                        "Si un compte existe pour cette adresse, tu vas recevoir un code à " +
                                            "$SUPABASE_WEB_EMAIL_OTP_DIGITS chiffres à saisir ci-dessous."
                                }
                            } finally {
                                busy = false
                            }
                        }
                    },
                    submitLabel = if (busy) "Connexion…" else "Se connecter",
                )
                if (!entry.showSentBanner) {
                    Button(attrs = {
                        attr("type", "button")
                        onClick {
                            error = null
                            successMessage = null
                            step = EmailAuthStep.CreateAccount
                        }
                        style { emailLoginSwitchChoice() }
                    }) { Text("En fait, je crée un compte") }
                }
                Button(attrs = {
                    attr("type", "button")
                    if (busy) attr("disabled", "true")
                    onClick {
                        error = null
                        successMessage = null
                        if (confirmationEmail.isBlank() && email.contains('@')) {
                            confirmationEmail = email.trim()
                        }
                        step = EmailAuthStep.ConfirmationLinkHelp
                    }
                    style { emailLoginSwitchChoice() }
                }) { Text("Je n’ai pas reçu le lien de confirmation") }
                Div({ style { passkeyCard() } }) {
                    P({ style { emailLoginLabel() } }) { Text("Connexion biométrique (Passkey)") }
                    P({ style { passkeyBody() } }) {
                        if (passkeyBlockedByPolicy) {
                            Text(
                                "Après une réinitialisation de mot de passe, la première connexion doit se faire " +
                                    "avec e-mail + mot de passe.",
                            )
                        } else if (!passkeyHintEnabled) {
                            Text(
                                "La biométrie n'est disponible qu'après une première connexion classique " +
                                    "et activation depuis les paramètres.",
                            )
                        } else if (passkeySupported) {
                            Text(
                                "Sur mobile, cette option utilise Face ID / Touch ID / empreinte / code appareil " +
                                    "selon ton téléphone et ton navigateur.",
                            )
                        } else {
                            Text("Ton navigateur ne semble pas compatible passkey sur cet appareil.")
                        }
                    }
                    Button(attrs = {
                        attr("type", "button")
                        if (!passkeySupported || busy || !passkeyHintEnabled || passkeyBlockedByPolicy) {
                            attr("disabled", "true")
                        }
                        onClick {
                            if (busy) return@onClick
                            scope.launch {
                                busy = true
                                error = null
                                successMessage = null
                                try {
                                    val em = email.trim()
                                    if (em.isEmpty() || !em.contains('@')) {
                                        error = "Indique d'abord ton e-mail pour la connexion biométrique."
                                        return@launch
                                    }
                                    val passkey = authenticateWithPasskey(em)
                                    if (passkey.session == null) {
                                        val msg = passkey.errorMessage ?: "Connexion biométrique impossible sur cet appareil."
                                        error = msg
                                    } else {
                                        saveSupabaseSessionFromPasskey(passkey.session)
                                        localStorage.setItem(PASSKEY_ENABLED_HINT_KEY, "1")
                                        localStorage.setItem(LAST_LOGIN_EMAIL_KEY, em)
                                        passkeyHintEnabled = true
                                        markInTabAppUnlockDone()
                                        onLoggedIn()
                                    }
                                } finally {
                                    busy = false
                                }
                            }
                        }
                        style { emailLoginSecondary() }
                    }) { Text("Se connecter avec biométrie") }
                }
            }
            EmailAuthStep.ResetPassword -> {
                H1({ style { emailLoginTitle() } }) {
                    Text("Nouveau mot de passe")
                }
                P({ style { emailLoginBody() } }) {
                    if (recoveryFlowActive) {
                        Text(
                            "Réinitialisation validée. Saisis ton nouveau mot de passe, " +
                                "confirme-le, puis valide pour revenir à la connexion.",
                        )
                    } else {
                        Text(
                            "Saisis ton code de vérification reçu par e-mail, puis " +
                                "tes deux nouveaux mots de passe et valide.",
                        )
                    }
                }
                val lockResetEmail =
                    recoveryFlowActive && email.trim().isNotBlank()
                emailResetPasswordForm(
                    email = email,
                    onEmail = { email = it },
                    lockEmailField = lockResetEmail,
                    otpCode = recoveryOtpCode,
                    onOtpCode = { recoveryOtpCode = it.trim().take(2048) },
                    needsOtpVerification = !recoveryFlowActive,
                    password = password,
                    onPassword = { password = it },
                    passwordConfirm = passwordConfirm,
                    onPasswordConfirm = { passwordConfirm = it },
                    busy = busy,
                    error = error,
                    successMessage = successMessage,
                    onSubmit = {
                        if (busy) return@emailResetPasswordForm
                        scope.launch {
                            busy = true
                            error = null
                            successMessage = null
                            try {
                                val em = email.trim()
                                if (em.isEmpty() || !em.contains('@')) {
                                    error = "Indique d'abord ton e-mail pour vérifier le code."
                                    return@launch
                                }
                                // Session recovery déjà ouverte (ex. lien e-mail consommé au chargement) :
                                // ne pas rappeler /verify avec un OTP vide (échec ou double consommation).
                                val mustVerifyOtp =
                                    !recoveryFlowActive || recoveryOtpCode.isNotBlank()
                                if (mustVerifyOtp) {
                                    val codeErr =
                                        verifySupabaseRecoveryOtpCode(client, em, recoveryOtpCode)
                                    if (codeErr != null) {
                                        error = codeErr
                                        return@launch
                                    }
                                }
                                recoveryFlowActive = true
                                if (password != passwordConfirm) {
                                    error = "Les deux mots de passe ne sont pas identiques."
                                    return@launch
                                }
                                val err = updateSupabasePasswordAfterRecovery(client, password)
                                if (err == null) {
                                    clearRecoveryFlowFlags()
                                    recoveryNeedsUserConfirm = false
                                    recoveryFlowActive = false
                                    successMessage = "Mot de passe mis à jour avec succès. Tu peux maintenant te connecter."
                                    step = EmailAuthStep.ExistingAccount
                                } else {
                                    error = err
                                }
                            } finally {
                                busy = false
                            }
                        }
                    },
                    submitLabel = if (busy) "Mise à jour…" else "Valider",
                )
                Button(attrs = {
                    attr("type", "button")
                    if (busy) attr("disabled", "true")
                    onClick {
                        clearRecoveryFlowFlags()
                        recoveryFlowActive = false
                        step = EmailAuthStep.ExistingAccount
                    }
                    style { emailLoginSecondary() }
                }) { Text("Retour à la connexion") }
                Button(attrs = {
                    attr("type", "button")
                    if (busy) attr("disabled", "true")
                    onClick {
                        if (busy) return@onClick
                        scope.launch {
                            busy = true
                            error = null
                            successMessage = null
                            try {
                                val em = email.trim()
                                if (em.isEmpty() || !em.contains('@')) {
                                    error = "Indique d'abord ton e-mail pour renvoyer un code."
                                    return@launch
                                }
                                val err = sendSupabasePasswordResetEmail(client, em)
                                if (err == null) {
                                    successMessage = "Nouveau code de vérification envoyé à $em."
                                } else {
                                    error = err
                                }
                            } finally {
                                busy = false
                            }
                        }
                    }
                    style { emailLoginSecondary() }
                }) { Text("Renvoyer un code de vérification") }
            }
            EmailAuthStep.RecoveryLinkGate -> {
                H1({ style { emailLoginTitle() } }) {
                    Text("Confirmer la réinitialisation")
                }
                P({ style { emailLoginBody() } }) {
                    Text(
                        "Pour eviter la pre-ouverture automatique du lien par certains clients mail, " +
                            "appuie sur le bouton ci-dessous pour continuer.",
                    )
                }
                if (error != null) {
                    P({ style { emailLoginError() } }) { Text(error!!) }
                }
                Button(attrs = {
                    attr("type", "button")
                    if (busy) attr("disabled", "true")
                    onClick {
                        if (busy) return@onClick
                        scope.launch {
                            busy = true
                            error = null
                            successMessage = null
                            try {
                                consumeSupabaseAuthRedirect(client, allowRecoveryConsumption = true)
                                if (hasRecoveryUiLock() || hasRecoveryPendingFlag()) {
                                    recoveryNeedsUserConfirm = false
                                    recoveryFlowActive = true
                                    step = EmailAuthStep.ResetPassword
                                } else {
                                    error =
                                        "Lien invalide ou deja utilise. Demande un nouveau lien " +
                                            "puis ouvre-le immediatement dans ton navigateur."
                                }
                            } catch (_: Throwable) {
                                error =
                                    "Impossible de verifier le lien pour le moment. " +
                                        "Reessaie dans un instant."
                            } finally {
                                busy = false
                            }
                        }
                    }
                    style { emailLoginButton() }
                }) { Text(if (busy) "Verification…" else "Continuer la reinitialisation") }
                Button(attrs = {
                    attr("type", "button")
                    if (busy) attr("disabled", "true")
                    onClick {
                        recoveryNeedsUserConfirm = false
                        recoveryFlowActive = false
                        clearRecoveryFlowFlags()
                        step = EmailAuthStep.ExistingAccount
                    }
                    style { emailLoginSecondary() }
                }) { Text("Retour a la connexion") }
            }
            EmailAuthStep.ConfirmationLinkHelp -> {
                LaunchedEffect(step, email) {
                    if (step == EmailAuthStep.ConfirmationLinkHelp &&
                        confirmationEmail.isBlank() &&
                        email.contains('@')
                    ) {
                        confirmationEmail = email.trim()
                    }
                }
                Button(attrs = {
                    attr("type", "button")
                    onClick {
                        error = null
                        successMessage = null
                        step = confirmationHelpBackStep
                    }
                    style { emailLoginBack() }
                }) { Text("← Retour") }
                H1({ style { emailLoginTitle() } }) {
                    Text("Lien de confirmation")
                }
                P({ style { emailLoginBody() } }) {
                    Text(
                        "Saisis l’adresse utilisée à l’inscription et le code à $SUPABASE_WEB_EMAIL_OTP_DIGITS chiffres reçu par e-mail. " +
                            "Tu peux aussi ouvrir le lien dans le mail pour activer le compte.",
                    )
                }
                P({ style { emailLoginLabel() } }) { Text("Adresse e-mail") }
                Input(type = InputType.Email, attrs = {
                    value(confirmationEmail)
                    onInput {
                        confirmationEmail = it.value
                        val em = confirmationEmail.trim()
                        if (em.contains('@')) {
                            localStorage.setItem(SIGNUP_LINK_PENDING_EMAIL_KEY, em)
                        }
                    }
                    attr("onfocus", FORM_FOCUS_SCROLL_JS)
                    attr("onkeydown", FORM_ENTER_NEXT_JS)
                    attr("autocomplete", "email")
                    attr("placeholder", "exemple@mail.com")
                    if (busy) attr("disabled", "true")
                    style { emailLoginInput() }
                })
                P({ style { emailLoginLabel() } }) { Text("Code de confirmation") }
                Input(type = InputType.Text, attrs = {
                    value(signupOtpCode)
                    onInput { signupOtpCode = it.value.filter { ch -> ch.isDigit() }.take(SUPABASE_WEB_EMAIL_OTP_DIGITS) }
                    attr("onfocus", FORM_FOCUS_SCROLL_JS)
                    attr("onkeydown", FORM_ENTER_NEXT_JS)
                    attr("autocomplete", "one-time-code")
                    attr("inputmode", "numeric")
                    attr("placeholder", "0".repeat(SUPABASE_WEB_EMAIL_OTP_DIGITS))
                    if (busy) attr("disabled", "true")
                    style { emailLoginInput() }
                })
                if (successMessage != null) {
                    P({ style { emailLoginInfoHint() } }) { Text(successMessage!!) }
                }
                if (error != null) {
                    P({ style { emailLoginError() } }) { Text(error!!) }
                }
                Button(attrs = {
                    attr("type", "button")
                    if (busy || signupOtpCode.length != SUPABASE_WEB_EMAIL_OTP_DIGITS || !confirmationEmail.contains('@')) {
                        attr("disabled", "true")
                    }
                    onClick {
                        if (busy) return@onClick
                        scope.launch {
                            busy = true
                            error = null
                            successMessage = null
                            try {
                                val em = confirmationEmail.trim()
                                if (em.isEmpty() || !em.contains('@')) {
                                    error = "Indique une adresse e-mail valide."
                                    return@launch
                                }
                                val err = verifySupabaseSignupOtpCode(client, em, signupOtpCode)
                                if (err == null) {
                                    markOpenParentProfileOncePending()
                                    successMessage = "Code confirmé. Ton compte est activé."
                                    if (hasSupabaseUserSession(client)) {
                                        markInTabAppUnlockDone()
                                        onLoggedIn()
                                    } else {
                                        step = EmailAuthStep.ExistingAccount
                                    }
                                } else {
                                    error = err
                                }
                            } finally {
                                busy = false
                            }
                        }
                    }
                    style { emailLoginSecondary() }
                }) { Text(if (busy) "Vérification…" else "Valider le code de confirmation") }
                Button(attrs = {
                    attr("type", "button")
                    if (busy || !resendAllowed) attr("disabled", "true")
                    onClick {
                        if (busy || !resendAllowed) return@onClick
                        scope.launch {
                            busy = true
                            error = null
                            successMessage = null
                            try {
                                val em = confirmationEmail.trim()
                                if (em.isEmpty() || !em.contains('@')) {
                                    error = "Indique une adresse e-mail valide."
                                    return@launch
                                }
                                val err = resendSupabaseSignupConfirmationEmail(client, em)
                                if (err == null) {
                                    val nextAt = js("Date.now()").unsafeCast<Double>().toLong() + SIGNUP_RESEND_COOLDOWN_MS
                                    resendAvailableAtMs = nextAt
                                    localStorage.setItem(SIGNUP_LINK_RESEND_AT_MS_KEY, nextAt.toString())
                                    successMessage = "Nouveau lien envoyé à $em."
                                } else {
                                    error = err
                                }
                            } finally {
                                busy = false
                            }
                        }
                    }
                    style { emailLoginButton() }
                }) {
                    if (!resendAllowed) {
                        Text("Renvoyer le lien (${resendRemainingSeconds}s)")
                    } else {
                        Text(if (busy) "Envoi..." else "Renvoyer le lien de confirmation")
                    }
                }
                Button(attrs = {
                    attr("type", "button")
                    onClick {
                        step = EmailAuthStep.ExistingAccount
                        successMessage = null
                        error = null
                    }
                    style { emailLoginSecondary() }
                }) { Text("Aller à la connexion") }
            }
        }
    }

    if (confirmationDialogOpen) {
        ConfirmationLinkDialog(
            email = confirmationEmail,
            busy = busy,
            canResend = resendAllowed,
            resendSeconds = resendRemainingSeconds,
            successMessage = successMessage,
            error = error,
            onResend = {
                if (busy || !resendAllowed) {
                    Unit
                } else {
                scope.launch {
                    busy = true
                    error = null
                    successMessage = null
                    try {
                        val em = confirmationEmail.trim()
                        if (em.isEmpty()) {
                            error = "Adresse e-mail invalide pour le renvoi."
                            return@launch
                        }
                        val err = resendSupabaseSignupConfirmationEmail(client, em)
                        if (err == null) {
                            val nextAt = js("Date.now()").unsafeCast<Double>().toLong() + SIGNUP_RESEND_COOLDOWN_MS
                            resendAvailableAtMs = nextAt
                            localStorage.setItem(SIGNUP_LINK_RESEND_AT_MS_KEY, nextAt.toString())
                            successMessage = "Nouveau lien envoyé à $em."
                        } else {
                            error = err
                        }
                    } finally {
                        busy = false
                    }
                }
                }
            },
            onOpenMail = { window.open("https://mail.google.com", "_blank") },
            onGoToLogin = {
                confirmationDialogOpen = false
                step = EmailAuthStep.ExistingAccount
                successMessage = null
                error = null
            },
        )
    }
}

@Composable
private fun emailResetPasswordForm(
    email: String,
    onEmail: (String) -> Unit,
    lockEmailField: Boolean,
    otpCode: String,
    onOtpCode: (String) -> Unit,
    needsOtpVerification: Boolean,
    password: String,
    onPassword: (String) -> Unit,
    passwordConfirm: String,
    onPasswordConfirm: (String) -> Unit,
    busy: Boolean,
    error: String?,
    successMessage: String?,
    onSubmit: () -> Unit,
    submitLabel: String,
) {
    var showPassword by remember { mutableStateOf(false) }
    var showPasswordConfirm by remember { mutableStateOf(false) }
    val passwordFieldsTouched = password.isNotBlank() || passwordConfirm.isNotBlank()
    val passwordsMatch = password.isNotBlank() && password == passwordConfirm
    val showLiveMismatch = passwordFieldsTouched && !passwordsMatch

    P({ style { emailLoginLabel() } }) { Text("Adresse e-mail") }
    Input(type = InputType.Email, attrs = {
        value(email)
        onInput { if (!lockEmailField) onEmail(it.value) }
        if (busy || lockEmailField) attr("disabled", "true")
        attr("onfocus", FORM_FOCUS_SCROLL_JS)
        attr("onkeydown", FORM_ENTER_NEXT_JS)
        attr("autocomplete", "email")
        style { emailLoginInput() }
    })
    if (needsOtpVerification) {
        P({ style { emailLoginLabel() } }) { Text("Code de vérification") }
        Input(type = InputType.Text, attrs = {
            value(otpCode)
            onInput { onOtpCode(it.value) }
            attr("onfocus", FORM_FOCUS_SCROLL_JS)
            attr("onkeydown", FORM_ENTER_NEXT_JS)
            attr("autocomplete", "one-time-code")
            attr("inputmode", "numeric")
            attr("placeholder", "0".repeat(SUPABASE_WEB_EMAIL_OTP_DIGITS))
            if (busy) attr("disabled", "true")
            style { emailLoginInput() }
        })
        P({ style { emailLoginInfoHint() } }) {
            Text(
                "Entre les $SUPABASE_WEB_EMAIL_OTP_DIGITS chiffres reçus par e-mail, ou colle le lien complet du message.",
            )
        }
    }
    P({ style { emailLoginLabel() } }) { Text("Nouveau mot de passe") }
    Div({ style { passwordInputWrap() } }) {
        Input(type = if (showPassword) InputType.Text else InputType.Password, attrs = {
            value(password)
            onInput { onPassword(it.value) }
            attr("onfocus", FORM_FOCUS_SCROLL_JS)
            attr("onkeydown", FORM_ENTER_NEXT_JS)
            attr("autocomplete", "new-password")
            if (busy) attr("disabled", "true")
            style { emailLoginInputWithIcon() }
        })
        Button(attrs = {
            attr("type", "button")
            if (busy) attr("disabled", "true")
            onClick { showPassword = !showPassword }
            style { passwordEyeButton() }
        }) { Text("👁") }
    }
    P({ style { emailLoginLabel() } }) { Text("Confirme ton mot de passe") }
    Div({ style { passwordInputWrap() } }) {
        Input(type = if (showPasswordConfirm) InputType.Text else InputType.Password, attrs = {
            value(passwordConfirm)
            onInput { onPasswordConfirm(it.value) }
            attr("onfocus", FORM_FOCUS_SCROLL_JS)
            attr("onkeydown", FORM_ENTER_NEXT_JS)
            attr("autocomplete", "new-password")
            if (busy) attr("disabled", "true")
            style { emailLoginInputWithIcon() }
        })
        Button(attrs = {
            attr("type", "button")
            if (busy) attr("disabled", "true")
            onClick { showPasswordConfirm = !showPasswordConfirm }
            style { passwordEyeButton() }
        }) { Text("👁") }
    }
    if (showLiveMismatch) {
        P({ style { emailLoginError() } }) {
            Text("Les deux mots de passe doivent être identiques.")
        }
    } else if (passwordsMatch) {
        P({ style { emailLoginInfoHint() } }) {
            Text("Les mots de passe correspondent.")
        }
    }
    if (successMessage != null) {
        P({ style { emailLoginInfoHint() } }) { Text(successMessage) }
    }
    if (error != null) {
        P({ style { emailLoginError() } }) { Text(error) }
    }
    Button(attrs = {
        attr("type", "button")
        if (busy || !passwordsMatch || (needsOtpVerification && !recoveryOtpFieldReadyForSubmit(otpCode))) {
            attr("disabled", "true")
        }
        onClick { onSubmit() }
        style { emailLoginButton() }
    }) { Text(submitLabel) }
}

private fun StyleScope.passwordInputWrap() {
    property("position", "relative")
    width(100.percent)
}

private fun StyleScope.emailLoginInputWithIcon() {
    emailLoginInput()
    property("padding-right", "44px")
}

private fun StyleScope.passwordEyeButton() {
    property("position", "absolute")
    property("right", "8px")
    property("top", "50%")
    property("transform", "translateY(-50%)")
    padding(6.px)
    property("min-width", "36px")
    property("min-height", "36px")
    color(Color("#E2E8F0"))
    backgroundColor(Color.transparent)
    property("border", "none")
    fontSize(16.px)
    property("white-space", "nowrap")
    property("cursor", "pointer")
}

@Composable
private fun ConfirmationLinkDialog(
    email: String,
    busy: Boolean,
    canResend: Boolean,
    resendSeconds: Long,
    successMessage: String?,
    error: String?,
    onResend: () -> Unit,
    onOpenMail: () -> Unit,
    onGoToLogin: () -> Unit,
) {
    Div({ style { confirmationOverlay() } }) {
        Div({ style { confirmationCard() } }) {
            H1({ style { emailLoginTitle() } }) { Text("Confirme ton e-mail") }
            P({ style { emailLoginBody() } }) {
                Text(
                    "Un e-mail a été envoyé à $email. Ouvre le lien reçu pour activer ton compte, " +
                        "puis reviens te connecter ici.",
                )
            }
            if (successMessage != null) {
                P({ style { emailLoginInfoHint() } }) { Text(successMessage) }
            }
            if (error != null) {
                P({ style { emailLoginError() } }) { Text(error) }
            }
            Button(attrs = {
                attr("type", "button")
                if (busy) attr("disabled", "true")
                onClick { onOpenMail() }
                style { emailLoginButton() }
            }) { Text("Ouvrir ma boîte mail") }
            Button(attrs = {
                attr("type", "button")
                if (busy || !canResend) attr("disabled", "true")
                onClick { onResend() }
                style { emailLoginSecondary() }
            }) {
                if (!canResend) {
                    Text("Renvoyer le lien (${resendSeconds}s)")
                } else {
                    Text(if (busy) "Envoi..." else "Renvoyer le lien")
                }
            }
            Button(attrs = {
                attr("type", "button")
                onClick { onGoToLogin() }
                style { emailLoginSecondary() }
            }) { Text("Aller à la connexion") }
        }
    }
}

@Composable
private fun emailPasswordSignInForm(
    email: String,
    onEmail: (String) -> Unit,
    password: String,
    onPassword: (String) -> Unit,
    busy: Boolean,
    error: String?,
    successMessage: String?,
    onSubmit: () -> Unit,
    onForgotPassword: () -> Unit,
    submitLabel: String,
) {
    var showPassword by remember { mutableStateOf(false) }
    P({ style { emailLoginLabel() } }) { Text("Adresse e-mail") }
    Input(type = InputType.Email, attrs = {
        value(email)
        onInput { onEmail(it.value) }
        attr("onfocus", FORM_FOCUS_SCROLL_JS)
        attr("onkeydown", FORM_ENTER_NEXT_JS)
        attr("autocomplete", "email")
        attr("placeholder", "toi@exemple.com")
        if (busy) attr("disabled", "true")
        style { emailLoginInput() }
    })
    P({ style { emailLoginInfoHint() } }) {
        Text("Utilise l'adresse e-mail de ton compte.")
    }
    P({ style { emailLoginLabel() } }) { Text("Mot de passe") }
    Div({ style { passwordInputWrap() } }) {
        Input(type = if (showPassword) InputType.Text else InputType.Password, attrs = {
            value(password)
            onInput { onPassword(it.value) }
            attr("onfocus", FORM_FOCUS_SCROLL_JS)
            attr("onkeydown", FORM_ENTER_NEXT_JS)
            attr("autocomplete", "current-password")
            if (busy) attr("disabled", "true")
            style { emailLoginInputWithIcon() }
        })
        Button(attrs = {
            attr("type", "button")
            if (busy) attr("disabled", "true")
            onClick { showPassword = !showPassword }
            style { passwordEyeButton() }
        }) { Text("👁") }
    }
    if (successMessage != null) {
        P({ style { emailLoginInfoHint() } }) { Text(successMessage!!) }
    }
    if (error != null) {
        P({ style { emailLoginError() } }) { Text(error!!) }
    }
    Button(attrs = {
        attr("type", "button")
        if (busy) attr("disabled", "true")
        onClick { onSubmit() }
        style { emailLoginButton() }
    }) { Text(submitLabel) }
    Button(attrs = {
        attr("type", "button")
        if (busy) attr("disabled", "true")
        onClick { onForgotPassword() }
        style { emailLoginSecondary() }
    }) { Text("Mot de passe oublié ?") }
}

@Composable
private fun emailPasswordSignUpForm(
    email: String,
    onEmail: (String) -> Unit,
    password: String,
    onPassword: (String) -> Unit,
    passwordConfirm: String,
    onPasswordConfirm: (String) -> Unit,
    busy: Boolean,
    error: String?,
    successMessage: String?,
    onSubmit: () -> Unit,
    submitLabel: String,
) {
    var showPassword by remember { mutableStateOf(false) }
    var showPasswordConfirm by remember { mutableStateOf(false) }
    val passwordFieldsTouched = password.isNotBlank() || passwordConfirm.isNotBlank()
    val passwordsMatch = password.isNotBlank() && password == passwordConfirm
    val showLiveMismatch = passwordFieldsTouched && !passwordsMatch

    P({ style { emailLoginLabel() } }) { Text("Adresse e-mail") }
    Input(type = InputType.Email, attrs = {
        value(email)
        onInput { onEmail(it.value) }
        attr("onfocus", FORM_FOCUS_SCROLL_JS)
        attr("onkeydown", FORM_ENTER_NEXT_JS)
        attr("autocomplete", "email")
        attr("placeholder", "toi@exemple.com")
        if (busy) attr("disabled", "true")
        style { emailLoginInput() }
    })
    P({ style { emailLoginInfoHint() } }) {
        Text("Utilise une adresse e-mail valide pour recevoir le lien de confirmation.")
    }
    P({ style { emailLoginLabel() } }) { Text("Mot de passe") }
    Div({ style { passwordInputWrap() } }) {
        Input(type = if (showPassword) InputType.Text else InputType.Password, attrs = {
            value(password)
            onInput { onPassword(it.value) }
            attr("onfocus", FORM_FOCUS_SCROLL_JS)
            attr("onkeydown", FORM_ENTER_NEXT_JS)
            attr("autocomplete", "new-password")
            if (busy) attr("disabled", "true")
            style { emailLoginInputWithIcon() }
        })
        Button(attrs = {
            attr("type", "button")
            if (busy) attr("disabled", "true")
            onClick { showPassword = !showPassword }
            style { passwordEyeButton() }
        }) { Text("👁") }
    }
    P({ style { emailLoginInfoHint() } }) {
        Text("Minimum 6 caractères.")
    }
    P({ style { emailLoginLabel() } }) { Text("Confirmer le mot de passe") }
    Div({ style { passwordInputWrap() } }) {
        Input(type = if (showPasswordConfirm) InputType.Text else InputType.Password, attrs = {
            value(passwordConfirm)
            onInput { onPasswordConfirm(it.value) }
            attr("onfocus", FORM_FOCUS_SCROLL_JS)
            attr("onkeydown", FORM_ENTER_NEXT_JS)
            attr("autocomplete", "new-password")
            if (busy) attr("disabled", "true")
            style { emailLoginInputWithIcon() }
        })
        Button(attrs = {
            attr("type", "button")
            if (busy) attr("disabled", "true")
            onClick { showPasswordConfirm = !showPasswordConfirm }
            style { passwordEyeButton() }
        }) { Text("👁") }
    }
    if (showLiveMismatch) {
        P({ style { emailLoginError() } }) {
            Text("Les deux mots de passe doivent être identiques.")
        }
    } else if (passwordsMatch) {
        P({ style { emailLoginInfoHint() } }) {
            Text("Les mots de passe correspondent.")
        }
    }
    if (successMessage != null) {
        P({ style { emailLoginInfoHint() } }) { Text(successMessage!!) }
    }
    if (error != null) {
        P({ style { emailLoginError() } }) { Text(error!!) }
    }
    Button(attrs = {
        attr("type", "button")
        if (busy || !passwordsMatch) attr("disabled", "true")
        onClick { onSubmit() }
        style { emailLoginButton() }
    }) { Text(submitLabel) }
}

private fun StyleScope.emailSentBanner() {
    width(100.percent)
    padding(14.px)
    margin(4.px)
    property("box-sizing", "border-box")
    property("border-radius", "14px")
    backgroundColor(Color("rgba(22, 101, 52, 0.45)"))
    property("border", "1px solid rgba(134, 239, 172, 0.55)")
}

private fun StyleScope.emailSentBannerTitle() {
    color(Color("#DCFCE7"))
    fontSize(17.px)
    margin(0.px)
    property("font-weight", "800")
}

private fun StyleScope.emailSentBannerBody() {
    color(Color("#ECFCCB"))
    fontSize(14.px)
    property("margin", "8px 0 0 0")
    property("line-height", "1.45")
}

private fun StyleScope.emailLoginCard() {
    display(DisplayStyle.Flex)
    flexDirection(FlexDirection.Column)
    alignItems(AlignItems.Stretch)
    gap(12.px)
    width(100.percent)
    padding(10.px)
}

private fun StyleScope.emailLoginHeroWrap() {
    display(DisplayStyle.Flex)
    justifyContent(JustifyContent.Center)
    width(100.percent)
    margin(4.px)
}

private fun StyleScope.emailLoginHeroImg() {
    width(180.px)
    maxWidth(68.percent)
    property("aspect-ratio", "1 / 1")
    property("object-fit", "cover")
    property("border-radius", "22px")
    property("box-shadow", "0 12px 40px rgba(250, 204, 21, 0.22)")
    property("border", "1px solid rgba(255,255,255,0.2)")
}

private fun StyleScope.emailLoginTitle() {
    color(Color.white)
    fontSize(24.px)
    margin(4.px)
    property("font-weight", "800")
}

private fun StyleScope.emailLoginBody() {
    color(Color("#E2E8F0"))
    fontSize(16.px)
    margin(4.px)
    property("line-height", "1.5")
}

private fun StyleScope.emailLoginLabel() {
    color(Color("#E2E8F0"))
    fontSize(15.px)
    margin(4.px)
    property("font-weight", "600")
}

private fun StyleScope.emailLoginInput() {
    width(100.percent)
    padding(12.px)
    backgroundColor(Color("rgba(255, 255, 255, 0.10)"))
    color(Color.white)
    property("border", "1px solid rgba(255,255,255,0.22)")
    property("border-radius", "14px")
    property("box-sizing", "border-box")
    property("font-size", "16px")
}

private fun StyleScope.emailLoginButton() {
    width(100.percent)
    margin(8.px)
    padding(14.px)
    property("min-height", "56px")
    property("line-height", "1.35")
    property("text-align", "center")
    property("white-space", "normal")
    color(Color.white)
    backgroundColor(Color("#0EA5E9"))
    property("border", "1px solid #7DD3FC")
    property("border-radius", "14px")
    property("font-weight", "700")
}

private fun StyleScope.emailLoginSecondary() {
    width(100.percent)
    margin(4.px)
    padding(12.px)
    property("min-height", "48px")
    property("line-height", "1.35")
    property("text-align", "center")
    property("white-space", "normal")
    color(Color("#E2E8F0"))
    backgroundColor(Color("rgba(255,255,255,0.08)"))
    property("border", "1px solid rgba(255,255,255,0.22)")
    property("border-radius", "14px")
    property("font-weight", "600")
    property("font-size", "16px")
}

private fun StyleScope.emailLoginError() {
    color(Color("#FCA5A5"))
    fontSize(14.px)
    margin(4.px)
    property("line-height", "1.4")
}

private fun StyleScope.emailLoginInfoHint() {
    color(Color("#A7F3D0"))
    fontSize(14.px)
    margin(4.px)
    property("line-height", "1.45")
}

private fun StyleScope.emailLoginLandingPrimary() {
    width(100.percent)
    margin(10.px)
    padding(18.px)
    property("min-height", "64px")
    property("line-height", "1.35")
    property("text-align", "center")
    property("white-space", "normal")
    color(Color.white)
    backgroundColor(Color("#0EA5E9"))
    property("border", "1px solid #7DD3FC")
    property("border-radius", "16px")
    property("font-weight", "800")
    fontSize(17.px)
}

private fun StyleScope.emailLoginLandingSecondary() {
    width(100.percent)
    margin(4.px)
    padding(16.px)
    property("min-height", "56px")
    property("line-height", "1.35")
    property("text-align", "center")
    property("white-space", "normal")
    color(Color("#F1F5F9"))
    backgroundColor(Color("rgba(255,255,255,0.10)"))
    property("border", "1px solid rgba(255,255,255,0.28)")
    property("border-radius", "16px")
    property("font-weight", "700")
    fontSize(16.px)
}

private fun StyleScope.emailLoginBack() {
    property("align-self", "flex-start")
    margin(0.px)
    padding(10.px)
    color(Color("#BAE6FD"))
    backgroundColor(Color.transparent)
    property("border", "none")
    property("font-weight", "600")
    property("cursor", "pointer")
    property("min-height", "44px")
    property("font-size", "16px")
    property("text-decoration", "underline")
    property("text-underline-offset", "3px")
}

/** Bascule inscription ↔ connexion sans repasser par l’écran d’accueil à deux boutons. */
private fun StyleScope.emailLoginSwitchChoice() {
    width(100.percent)
    margin(8.px, 4.px, 0.px, 4.px)
    padding(12.px)
    color(Color("#DDD6FE"))
    backgroundColor(Color.transparent)
    property("border", "none")
    property("font-weight", "600")
    property("font-size", "16px")
    property("min-height", "44px")
    property("cursor", "pointer")
    property("text-decoration", "underline")
    property("text-underline-offset", "3px")
}

private fun StyleScope.confirmationOverlay() {
    property("position", "fixed")
    property("inset", "0")
    property("z-index", "9999")
    backgroundColor(Color("rgba(2, 6, 23, 0.72)"))
    display(DisplayStyle.Flex)
    justifyContent(JustifyContent.Center)
    alignItems(AlignItems.Center)
    padding(16.px)
}

private fun StyleScope.confirmationCard() {
    width(100.percent)
    maxWidth(560.px)
    property("max-height", "90vh")
    property("overflow-y", "auto")
    padding(18.px)
    backgroundColor(Color("#1a1028"))
    property("border", "1px solid rgba(148,163,184,0.35)")
    property("border-radius", "18px")
    display(DisplayStyle.Flex)
    flexDirection(FlexDirection.Column)
    gap(10.px)
    property("box-shadow", "0 20px 45px rgba(2,6,23,0.5)")
}

private fun StyleScope.passkeyCard() {
    width(100.percent)
    margin(8.px, 0.px, 0.px, 0.px)
    padding(12.px)
    backgroundColor(Color("rgba(14, 165, 233, 0.12)"))
    property("border", "1px solid rgba(125, 211, 252, 0.45)")
    property("border-radius", "14px")
    property("box-sizing", "border-box")
    display(DisplayStyle.Flex)
    flexDirection(FlexDirection.Column)
    gap(8.px)
}

private fun StyleScope.passkeyBody() {
    color(Color("#BFDBFE"))
    fontSize(14.px)
    margin(0.px)
    property("line-height", "1.45")
}

private fun isWebAuthnSupportedInBrowser(): Boolean =
    js(
        "typeof window !== 'undefined' && " +
            "typeof window.PublicKeyCredential !== 'undefined' && " +
            "window.isSecureContext === true",
    ).unsafeCast<Boolean>()

private suspend fun authenticateWithPasskey(email: String): PasskeyAuthResult {
    if (!isSupabaseWebConfigured()) return PasskeyAuthResult(errorMessage = "Supabase n’est pas configuré.")
    val base = supabaseWebUrl() ?: return PasskeyAuthResult(errorMessage = "Configuration Supabase incomplète.")
    val anon = supabaseWebAnonKey() ?: return PasskeyAuthResult(errorMessage = "Configuration Supabase incomplète.")
    val begin = passkeyFunctionCall(
        base = base,
        anon = anon,
        action = "begin_auth",
        payload = js("{ email: email }"),
    )
    if (!begin.ok) return PasskeyAuthResult(errorMessage = begin.error)
    val challengeId = begin.parsed?.challenge_id as? String
    if (!challengeId.isNullOrBlank()) {
        sessionStorage.setItem(PASSKEY_LAST_CHALLENGE_ID_KEY, challengeId)
    } else {
        sessionStorage.removeItem(PASSKEY_LAST_CHALLENGE_ID_KEY)
    }
    val options = begin.parsed?.options ?: return PasskeyAuthResult(errorMessage = "Options passkey introuvables.")
    val credential = browserGetPasskeyCredential(options) ?: return PasskeyAuthResult(errorMessage = "Action biométrique annulée.")
    val challengePayload = js("{ email: email, credential: credential }")
    val lastChallengeId = sessionStorage.getItem(PASSKEY_LAST_CHALLENGE_ID_KEY)?.trim().orEmpty()
    if (lastChallengeId.isNotBlank()) {
        challengePayload.challenge_id = lastChallengeId
    }
    val finish = passkeyFunctionCall(
        base = base,
        anon = anon,
        action = "finish_auth",
        payload = challengePayload,
    )
    if (!finish.ok) return PasskeyAuthResult(errorMessage = finish.error)
    val session = finish.parsed?.session ?: return PasskeyAuthResult(errorMessage = "Session passkey manquante.")
    val access = session.access_token as? String ?: return PasskeyAuthResult(errorMessage = "Session invalide.")
    val refresh = session.refresh_token as? String ?: return PasskeyAuthResult(errorMessage = "Session invalide.")
    val userId = session.user_id as? String ?: return PasskeyAuthResult(errorMessage = "Session invalide.")
    val expiresAt = (session.expires_at as? Number)?.toLong()
    return PasskeyAuthResult(
        session = PasskeySessionPayload(
            accessToken = access,
            refreshToken = refresh,
            userId = userId,
            expiresAt = expiresAt,
            email = email,
        ),
    )
}

private suspend fun passkeyFunctionCall(
    base: String,
    anon: String,
    action: String,
    payload: dynamic,
): PasskeyFunctionCallResult {
    val requestPayload = js("{}")
    requestPayload.action = action
    if (payload != null) {
        js("Object.assign")(requestPayload, payload)
    }
    val headers = js("{}")
    headers["Content-Type"] = "application/json"
    headers["apikey"] = anon
    headers["Authorization"] = "Bearer $anon"
    val init = js("{}")
    init.method = "POST"
    init.headers = headers
    init.body = JSON.stringify(requestPayload)
    val response = window.fetch("$base/functions/v1/webauthn-passkey", init).await()
    val text = response.text().await()
    val parsed = try {
        JSON.parse<dynamic>(text)
    } catch (_: Throwable) {
        null
    }
    if (!response.ok) {
        val raw = (parsed?.error as? String)?.trim().orEmpty()
        val details = (parsed?.details as? String)?.trim().orEmpty()
        val friendly = when {
            "no passkey" in raw -> "Aucune passkey n'est enregistrée pour cet e-mail."
            "credential not found" in raw -> "Aucune passkey correspondante n'a été trouvée."
            "credential_corrupted" in raw -> "Passkey invalide détectée. Réinitialise la biométrie puis réessaie."
            "begin_auth_failed" in raw -> "Initialisation biométrique impossible. Réinitialise la biométrie puis réessaie."
            "no active challenge" in raw -> "Session biométrique expirée. Réessaie."
            "verification failed" in raw ->
                "Vérification biométrique refusée." +
                    details.takeIf { it.isNotBlank() }?.let { " ($it)" }.orEmpty()
            "session_from_passkey_failed" in raw -> "Connexion biométrique validée, mais création de session impossible. Réessaie."
            "passkey_unhandled_error" in raw ->
                "Erreur biométrique temporaire. Réessaie ou réinitialise la biométrie." +
                    details.takeIf { it.isNotBlank() }?.let { " ($it)" }.orEmpty()
            raw.isNotBlank() -> "Biométrie: $raw"
            else -> "Connexion biométrique refusée."
        }
        return PasskeyFunctionCallResult(ok = false, parsed = parsed, error = friendly)
    }
    return PasskeyFunctionCallResult(ok = true, parsed = parsed)
}

private suspend fun browserGetPasskeyCredential(options: dynamic): dynamic {
    val publicKey = js("Object.assign({}, options)")
    publicKey.challenge = b64UrlToUint8Array(options.challenge as String)
    val allow = options.allowCredentials
    if (allow != null && js("Array.isArray(allow)") as Boolean) {
        val mapped = js("[]")
        val len = (allow.length as Number).toInt()
        for (i in 0 until len) {
            val c = allow[i]
            val item = js("{}")
            item.type = c.type
            item.id = b64UrlToUint8Array(c.id as String)
            item.transports = c.transports
            mapped.push(item)
        }
        publicKey.allowCredentials = mapped
    }
    val request = js("{}")
    request.publicKey = publicKey
    val cred = window.navigator.asDynamic().credentials.get(request).unsafeCast<Promise<dynamic>>().await() ?: return null
    return credentialToSerializableJson(cred)
}

private fun b64UrlToUint8Array(b64url: String): dynamic =
    run {
        val b64 = b64url.replace('-', '+').replace('_', '/')
        val pad = "=".repeat((4 - (b64.length % 4)) % 4)
        val binary = js("atob")(b64 + pad) as String
        val out = js("new Uint8Array(binary.length)")
        for (i in binary.indices) {
            out[i] = binary[i].code
        }
        out
    }

private fun arrayBufferToB64Url(buf: dynamic): String {
    val bytes = js("new Uint8Array(buf)")
    val len = (bytes.length as Number).toInt()
    val sb = StringBuilder()
    for (i in 0 until len) {
        sb.append((bytes[i] as Number).toInt().toChar())
    }
    val b64 = js("btoa")(sb.toString()) as String
    return b64.replace('+', '-').replace('/', '_').trimEnd('=')
}

private fun credentialToSerializableJson(cred: dynamic): dynamic {
    val r = cred.response
    val out = js("{}")
    out.id = cred.id
    out.type = cred.type
    out.rawId = arrayBufferToB64Url(cred.rawId)
    out.response = js("{}")
    out.response.authenticatorData = arrayBufferToB64Url(r.authenticatorData)
    out.response.clientDataJSON = arrayBufferToB64Url(r.clientDataJSON)
    out.response.signature = arrayBufferToB64Url(r.signature)
    out.response.userHandle = if (r.userHandle != null) arrayBufferToB64Url(r.userHandle) else null
    return out
}
